import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Formik } from 'formik';
import { RepliconDialog } from '~/modules/common/components';
import { PERIOD_SCALE_ENUM } from '~/modules/resourcing/common/enums';
import ShowSmartFitDialogOrLoading2 from '~/modules/resourcing/components/ResourceAssignmentDialog/components/ResourceUsersChartPanel/components/ShowSmartFitDialogOrLoading/ShowSmartFitDialogOrLoading2';
import SetResourceAllocationDialogTitle from './SetResourceAllocationDialogTitle';
import SetResourceAllocationDialogContent from './SetResourceAllocationDialogContent';
import SetResourceAllocationDialogFooter from './SetResourceAllocationDialogFooter';
import SetResourceAllocationDialogLoadingState from './SetResourceAllocationDialogLoadingState';
import { useSetResourceAllocationFormState } from './hooks/useSetResourceAllocationFormState';
import { useSetResourceAllocationOnSubmit } from './hooks/useSetResourceAllocationOnSubmit';

const SetResourceAllocationDialogTitleId =
  'set-resource-allocation-dialog-title';

const SetResourceAllocationDialog = ({
  existingResourceAllocation,
  isSaving,
  onClose,
  onUpdateResourceAllocation,
  open,
  project,
  scale,
  user
}) => {
  const {
    initialValues,
    validationSchema,
    totalScheduledHours,
    isScheduleLoading
  } = useSetResourceAllocationFormState({
    project,
    scale,
    userUri: user?.id
  });

  const {
    onFormSubmit,
    handleSetAllocation,
    isSmartFitOpen,
    smartFitSubmission,
    closeSmartFitDialog
  } = useSetResourceAllocationOnSubmit({
    existingResourceAllocation,
    userUri: user?.id,
    onClose,
    onUpdateResourceAllocation
  });
  const { formatMessage } = useIntl();
  const setAllocationLabel = formatMessage({
    id: 'resourceOverAllocationConfirmationDialog.setAllocation'
  });
  const [isFormDirty, setIsFormDirty] = useState(false);

  const checkIsDirty = useCallback(() => isFormDirty, [isFormDirty]);

  return (
    <RepliconDialog
      aria-labelledby={SetResourceAllocationDialogTitleId}
      checkIsDirty={checkIsDirty}
      data-qe-id="SetResourceAllocationDialog"
      enableUnsavedChangesWarning
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      trapFocusWithDatePicker
    >
      <SetResourceAllocationDialogTitle
        project={project}
        titleId={SetResourceAllocationDialogTitleId}
        user={user}
      />
      {isScheduleLoading ? (
        <SetResourceAllocationDialogLoadingState onClose={onClose} />
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={onFormSubmit}
          validationSchema={validationSchema}
        >
          <>
            <SetResourceAllocationDialogContent
              setIsFormDirty={setIsFormDirty}
              totalScheduledHours={totalScheduledHours}
              user={user}
            />
            <SetResourceAllocationDialogFooter
              onFormSubmit={onFormSubmit}
              isSmartFitOpen={isSmartFitOpen}
              isSaving={isSaving}
              onClose={onClose}
            />
          </>
        </Formik>
      )}
      {isSmartFitOpen && (
        <ShowSmartFitDialogOrLoading2
          confirmButtonLabel={setAllocationLabel}
          periodType={smartFitSubmission.periodType}
          resourceUser={user}
          scale={scale}
          totalAllocatedHours={smartFitSubmission.totalAllocatedHours}
          allocationDateRange={smartFitSubmission.allocationDateRange}
          createAllocationWithUserAndOverrideRules={handleSetAllocation}
          closeShowSmartFitDialogOrLoading={closeSmartFitDialog}
        />
      )}
    </RepliconDialog>
  );
};

SetResourceAllocationDialog.propTypes = {
  existingResourceAllocation: PropTypes.object,
  isSaving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onUpdateResourceAllocation: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  project: PropTypes.object,
  scale: PropTypes.oneOf(Object.values(PERIOD_SCALE_ENUM)).isRequired,
  user: PropTypes.object
};

export default SetResourceAllocationDialog;
